import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { Alert } from '@/components/Alert';
import { CheckBox } from '@/components/CheckBox';
import { FormInput } from '@/components/FormInput';
import { ContactInfoMobile } from '@/components/ContactInfoMobile';
import { BtnOneLineBlue } from '@/components/buttons/BtnOneLineBlue';
import { ModalLayout } from '@/features/modal/modals/ModalLayout';
import { COUNTRY_PHONE_PREFIX } from '@/constants/phonePrefixes';
import {
  selectUser,
  selectUserEmail,
  selectUserHasValidContactInfo,
} from '@/store/selectors/userSelectors';
import { updateConsentsSuccess } from '@/store/actions/consentsActions';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import BffApiService from '@/shared/bffApiService';
import { generateBffErrorMessageWithCode } from '@/utils/utils';
import { getUserSuccess } from '@/store/actions/user';
import { BffUserUpdateContactInfoResponse } from '@lucky7ventures/bff-types';
import { BaseModalProps } from '@/features/modal/types';

export const ContactInfoModalSweden = ({ closeModal }: BaseModalProps): JSX.Element => {
  const intl = useIntl();
  const user = useSelector(selectUser);
  const userEmail = useSelector(selectUserEmail);
  const [smsChecked, setSmsChecked] = useState(true);
  const [emailChecked, setEmailChecked] = useState(true);
  const hasValidContactInfo = useSelector(selectUserHasValidContactInfo);
  const dispatch = useDispatch();
  const { request, error, loading } = useBffApiRequest<BffUserUpdateContactInfoResponse>();

  const initialValues = {
    email: userEmail,
    mobile: {
      number: hasValidContactInfo && user ? user.mobile : '',
      country: hasValidContactInfo && user ? user.countryCode : '',
      prefix: hasValidContactInfo && user ? COUNTRY_PHONE_PREFIX[user.countryCode] : '',
      wholeNumber:
        hasValidContactInfo && user
          ? `${user.mobilePrefix}${user.mobile}`
          : user
          ? COUNTRY_PHONE_PREFIX[user.countryCode]
          : '',
    },
  };

  const validationSchema = Yup.object().shape({
    email: hasValidContactInfo
      ? Yup.string().notRequired()
      : Yup.string()
          .required(intl.formatMessage({ id: 'inputs.email.required' }))
          .email(intl.formatMessage({ id: 'inputs.email.invalid' })),
    mobile: hasValidContactInfo
      ? Yup.object().notRequired()
      : Yup.object().shape({
          wholeNumber: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
          number: Yup.string().min(5, intl.formatMessage({ id: 'completeProfile.mobile.error' })),
        }),
  });

  const submitHandler = (values: any): void => {
    request({
      apiMethod: BffApiService.updateContactInfo,
      payload: {
        email: values.email,
        mobileNumber: values.mobile.number,
        mobilePrefix: values.mobile.prefix,
        smsConsented: smsChecked,
        emailConsented: emailChecked,
      },
      successCallback: (data: BffUserUpdateContactInfoResponse) => {
        dispatch(getUserSuccess(data.user));
        dispatch(updateConsentsSuccess(data.consents));
        closeModal();
      },
    });
  };

  return (
    <ModalLayout close={closeModal} className="max-w-md">
      <div className="w-full">
        <h1 className="mb-8 text-2xl">
          {intl.formatMessage({ id: 'updateContactInfo.modal.title' })}
        </h1>
        <div className="content">
          <Formik
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={values => submitHandler(values)}
          >
            {({ isValid, handleSubmit }) => (
              <Form>
                <FormInput
                  name="email"
                  type="email"
                  label={intl.formatMessage({
                    id: 'inputs.email',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'inputs.email.placeholder',
                  })}
                  completed={!!userEmail}
                />
                <ContactInfoMobile
                  label={intl.formatMessage({ id: 'completeProfile.mobile.placeholder' })}
                  placeholder="+467212345678"
                  name="mobile"
                  completed={hasValidContactInfo}
                />
                {!hasValidContactInfo && (
                  <div className="consent mt-8 flex flex-col justify-between rounded-lg bg-white px-4 pt-4 pb-2 shadow-md">
                    <p className="mx-auto mb-4 max-w-[260px] text-sm">
                      {intl.formatMessage({ id: 'completeProfile.optin.header' })}
                    </p>
                    <div className="checkboxes flex items-center justify-around last:ml-4">
                      <CheckBox
                        id="smsOptIn"
                        value="checked"
                        label={intl.formatMessage({ id: 'inputs.toggle.sms' })}
                        initialCheck={smsChecked}
                        handleChange={() => setSmsChecked(prevValue => !prevValue)}
                        className="pt-1 pr-2 pl-8 text-xs font-bold text-slate-800 "
                      />
                      <CheckBox
                        id="emailOptIn"
                        value="checked"
                        label={intl.formatMessage({ id: 'inputs.toggle.email' })}
                        initialCheck={emailChecked}
                        handleChange={() => setEmailChecked(prevValue => !prevValue)}
                        className="pt-1 pr-2 pl-8 text-xs font-bold text-slate-800 "
                      />
                    </div>
                  </div>
                )}
                <div className="mt-8 mb-4">
                  <BtnOneLineBlue
                    onClickHandler={handleSubmit}
                    isLoading={loading}
                    disabled={!isValid}
                    text={intl.formatMessage({ id: 'misc.update' })}
                  />
                </div>
              </Form>
            )}
          </Formik>
          {error && (
            <Alert
              text={generateBffErrorMessageWithCode(
                intl.formatMessage({ id: 'error.support' }),
                error.code,
              )}
            />
          )}
        </div>
      </div>
    </ModalLayout>
  );
};
